import axios from 'axios';
import { clearAuthStorage, getAuthToken, setAuthToken } from '@/lib/authStorage';
import { emitAuthSessionRequired } from '@/lib/authEvents';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const REFRESH_THRESHOLD_MS = 2 * 24 * 60 * 60 * 1000;

let refreshPromise: Promise<string | null> | null = null;

export function isPublicAuthRoute(url?: string): boolean {
  if (!url) return false;
  return /\/auth\/(login|register|guest|refresh|verify-email|resend-verification|forgot-password|reset-password)(\/|$|\?)/.test(
    url,
  );
}

async function syncTokenToStore(
  token: string,
  user?: { id?: string; name: string; email: string; role?: 'user' | 'admin' },
) {
  const { useAuthStore } = await import('@/store/useAuthStore');
  if (user) {
    useAuthStore.setState({ token, isAuthenticated: true, user });
    return;
  }
  useAuthStore.getState().setToken(token);
}

export function getTokenExpiryMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function shouldRefreshToken(token: string): boolean {
  const expiry = getTokenExpiryMs(token);
  if (!expiry) return false;
  return expiry - Date.now() < REFRESH_THRESHOLD_MS;
}

export async function refreshAuthToken(): Promise<string | null> {
  const token = getAuthToken();
  if (!token) return null;

  if (!refreshPromise) {
    refreshPromise = axios
      .post<{ access_token: string; user?: unknown }>(`${API_URL}/auth/refresh`, null, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(async (response) => {
        const newToken = response.data.access_token;
        setAuthToken(newToken);
        await syncTokenToStore(
          newToken,
          response.data.user as { id?: string; name: string; email: string; role?: 'user' | 'admin' } | undefined,
        );
        return newToken;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

export async function ensureFreshToken(): Promise<boolean> {
  const token = getAuthToken();
  if (!token) return false;
  if (!shouldRefreshToken(token)) return true;

  const refreshed = await refreshAuthToken();
  return Boolean(refreshed);
}

export async function forceLogout(reason: 'expired' | 'invalid' = 'expired') {
  clearAuthStorage();

  const { useAuthStore } = await import('@/store/useAuthStore');
  const { isAuthenticated, logout } = useAuthStore.getState();
  if (isAuthenticated) logout();

  emitAuthSessionRequired(reason);
}
